import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  errorApiRef,
  useApi,
  type SignInPageProps,
} from '@backstage/core-plugin-api';
import {
  GitHubIcon,
  UserIdentity,
  type SignInProviderConfig,
} from '@backstage/core-components';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import SvgIcon from '@material-ui/core/SvgIcon';
import Typography from '@material-ui/core/Typography';
import {
  forgetSignInProvider,
  rememberSignInProvider,
  useSilentSignInRestore,
} from './useSilentSignInRestore';

const MAKE_LOGO_URL = 'https://cdn.make.com/img/make/make_app_white_logo.png';

const useStyles = makeStyles(theme => ({
  root: {
    minHeight: '100vh',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    boxSizing: 'border-box',
    background: [
      'radial-gradient(circle at 18% 22%, rgba(255,255,255,0.18), transparent 42%)',
      'radial-gradient(circle at 82% 78%, rgba(255,255,255,0.12), transparent 45%)',
      'linear-gradient(160deg, #4000cc 0%, #6d00cc 45%, #b55dcd 100%)',
    ].join(', '),
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: 420,
  },
  logo: {
    height: 38,
    width: 'auto',
    marginBottom: theme.spacing(4),
  },
  card: {
    width: '100%',
    boxSizing: 'border-box',
    background: theme.palette.background.paper,
    borderRadius: 16,
    boxShadow: '0 24px 64px rgba(9, 30, 69, 0.35)',
    padding: theme.spacing(5, 4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(4),
  },
  providers: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  providerButton: {
    justifyContent: 'flex-start',
    textTransform: 'none',
    padding: theme.spacing(1.5, 2),
    borderColor: theme.palette.divider,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      backgroundColor: 'rgba(109, 0, 204, 0.04)',
    },
  },
  providerLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    lineHeight: 1.3,
  },
  providerTitle: {
    fontWeight: 600,
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
  },
  providerMessage: {
    fontSize: '0.78rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
}));

const MicrosoftIcon = () => (
  <SvgIcon viewBox="0 0 23 23">
    <path fill="#f25022" d="M1 1h10v10H1z" />
    <path fill="#7fba00" d="M12 1h10v10H12z" />
    <path fill="#00a4ef" d="M1 12h10v10H1z" />
    <path fill="#ffb900" d="M12 12h10v10H12z" />
  </SvgIcon>
);

function providerIcon(id: string): ReactNode {
  if (id.includes('github')) return <GitHubIcon />;
  if (id.includes('microsoft')) return <MicrosoftIcon />;
  return undefined;
}

/**
 * A single branded provider action. Kept as its own component so the auth API
 * hook is called at the top level (rather than inside a `.map`), and so each
 * button can track its own in-progress state.
 */
function ProviderButton({
  provider,
  onSignInSuccess,
}: {
  provider: SignInProviderConfig;
  onSignInSuccess: SignInPageProps['onSignInSuccess'];
}) {
  const classes = useStyles();
  const authApi = useApi(provider.apiRef);
  const errorApi = useApi(errorApiRef);
  const [busy, setBusy] = useState(false);

  // Mirrors the ceremony Backstage's built-in provider card runs, so session
  // and identity behavior is identical to the default sign-in page.
  const handleLogin = async () => {
    try {
      setBusy(true);
      // Remember the chosen provider so the next visit can silently restore the
      // session for it (mirrors the built-in SignInPage's onSignInStarted).
      rememberSignInProvider(provider.id);
      const identityResponse = await authApi.getBackstageIdentity({
        instantPopup: true,
      });
      if (!identityResponse) {
        throw new Error(
          `The ${provider.title} provider is not configured to support sign-in`,
        );
      }
      const profile = await authApi.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          authApi,
          profile,
        }),
      );
    } catch (error) {
      setBusy(false);
      forgetSignInProvider();
      const cause = error instanceof Error ? error.message : String(error);
      errorApi.post(new Error(`Login failed: ${cause}`));
    }
  };

  return (
    <Button
      className={classes.providerButton}
      variant="outlined"
      fullWidth
      size="large"
      disabled={busy}
      onClick={handleLogin}
      startIcon={
        busy ? <CircularProgress size={20} /> : providerIcon(provider.id)
      }
    >
      <span className={classes.providerLabel}>
        <span className={classes.providerTitle}>
          Sign in with {provider.title}
        </span>
        {provider.message && (
          <span className={classes.providerMessage}>{provider.message}</span>
        )}
      </span>
    </Button>
  );
}

export type MakeSignInPageProps = SignInPageProps & {
  providers: SignInProviderConfig[];
  silentSignInProviderId?: string;
};

/**
 * Branded sign-in screen for the Make developer portal. Renders the enabled
 * auth providers as primary actions over a Make gradient, replacing the default
 * Backstage card grid while reusing Backstage's auth ceremony.
 */
export const MakeSignInPage = ({
  providers,
  onSignInSuccess,
  silentSignInProviderId,
}: MakeSignInPageProps) => {
  const classes = useStyles();
  // Reinstates the silent session restore the built-in SignInPage does
  // internally. Local auth can also nominate a provider that establishes its
  // initial session without an interactive OAuth flow.
  const restoring = useSilentSignInRestore({
    providers,
    onSignInSuccess,
    silentSignInProviderId,
  });

  if (restoring) {
    return (
      <div className={classes.root}>
        <div className={classes.column}>
          <img className={classes.logo} src={MAKE_LOGO_URL} alt="Make" />
          <CircularProgress style={{ color: '#fff' }} />
        </div>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.column}>
        <img className={classes.logo} src={MAKE_LOGO_URL} alt="Make" />
        <div className={classes.card}>
          <Typography variant="h5" component="h1" className={classes.title}>
            Sign in to Make Backstage
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            Choose how you want to sign in
          </Typography>
          <div className={classes.providers}>
            {providers.map(provider => (
              <ProviderButton
                key={provider.id}
                provider={provider}
                onSignInSuccess={onSignInSuccess}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
